<template>
  <Dialog :open="open" :dismissible="true" :showClose="true" title="Security Requests" :maxWidth="420" @close="onClose">
    <!-- Which site these requests belong to. The dialog is app-wide now, so it can
         open from any screen — name the site so the context is never ambiguous. -->
    <div v-if="siteName" class="site-line">
      <ion-icon name="business-outline" class="site-line__icon"></ion-icon>
      <span class="site-line__name">{{ siteName }}</span>
    </div>

    <div class="intro">
      <span class="intro__icon" v-html="shieldSvg"></span>
      <div>
        Your security company is requesting the following change(s).
        Respond before the timer ends, or the request is automatically declined.
      </div>
    </div>

    <div class="reqs">
      <!-- Camera bypass requests -->
      <div v-for="r in requests" :key="'bp-' + r.deviceId" class="req">
        <div class="req__head">
          <div class="req__name">{{ nameOf(r.deviceId) }}</div>
          <div class="req__timer" :class="{ 'req__timer--urgent': remainingMs(r) < 30000 }">
            <ion-icon name="time-outline" />
            <span>{{ remainingText(r) }}</span>
          </div>
        </div>
        <div class="req__detail">
          Bypass camera for <strong>{{ durationLabelOf(r) }}</strong>
          <span v-if="r.reason"> · {{ r.reason }}</span>
        </div>
        <div class="req__by">Requested by {{ r.initiatorName || 'Central Station' }}</div>
        <div class="req__actions">
          <button class="req-btn req-btn--reject" @click="reject(r.deviceId)">Decline</button>
          <button class="req-btn req-btn--approve" @click="approve(r.deviceId)">Approve</button>
        </div>
      </div>

      <!-- Clear-bypass requests (MC asks to clear an active bypass) -->
      <div v-for="r in clearRequests" :key="'cl-' + r.deviceId" class="req">
        <div class="req__head">
          <div class="req__name">{{ nameOf(r.deviceId) }}</div>
          <div class="req__timer" :class="{ 'req__timer--urgent': clearRemainingMs(r) < 30000 }">
            <ion-icon name="time-outline" />
            <span>{{ clearRemainingText(r) }}</span>
          </div>
        </div>
        <div class="req__detail">
          <strong>Clear bypass</strong> on this camera
          <span v-if="r.reason"> · {{ r.reason }}</span>
        </div>
        <div class="req__by">Requested by {{ r.initiatorName || 'Central Station' }}</div>
        <div class="req__actions">
          <button class="req-btn req-btn--reject" @click="clearReject(r.deviceId)">Decline</button>
          <button class="req-btn req-btn--approve" @click="clearApprove(r.deviceId)">Approve</button>
        </div>
      </div>

      <!-- Temporary-disarm requests (per partition) -->
      <div v-for="r in tdRequests" :key="'td-' + r.partitionId" class="req req--td">
        <div class="req__head">
          <div class="req__name">{{ partitionNameOf(r.partitionId) }}</div>
          <div class="req__timer req__timer--td" :class="{ 'req__timer--urgent': tdRemainingMs(r) < 30000 }">
            <ion-icon name="time-outline" />
            <span>{{ tdRemainingText(r) }}</span>
          </div>
        </div>
        <div class="req__detail">
          Temporarily disarm for <strong>{{ tdDurationLabelOf(r) }}</strong>
          <span v-if="r.reason"> · {{ r.reason }}</span>
        </div>
        <div class="req__by">Requested by {{ r.initiatorName || 'Central Station' }}</div>
        <div class="req__actions">
          <button class="req-btn req-btn--reject" @click="tdReject(r.partitionId)">Decline</button>
          <button class="req-btn req-btn--approve req-btn--approve-td" @click="tdApprove(r.partitionId)">Approve</button>
        </div>
      </div>

      <!-- Arm / Disarm requests (per partition). Arm carries the armed-orange
           accent, disarm the disarmed-green one, matching the arming UI. -->
      <div v-for="r in armingRequests" :key="'arm-' + r.partitionId" class="req" :class="r.action === 'arm' ? 'req--arm' : 'req--td'">
        <div class="req__head">
          <div class="req__name">{{ partitionNameOf(r.partitionId) }}</div>
          <div class="req__timer" :class="[{ 'req__timer--urgent': armingRemainingMs(r) < 30000 }, r.action === 'arm' ? 'req__timer--arm' : 'req__timer--td']">
            <ion-icon name="time-outline" />
            <span>{{ armingRemainingText(r) }}</span>
          </div>
        </div>
        <div class="req__detail">
          <strong>{{ r.action === 'disarm' ? 'Disarm' : 'Arm' }}</strong> this partition
          <span v-if="r.reason"> · {{ r.reason }}</span>
        </div>
        <div class="req__by">Requested by {{ r.initiatorName || 'Central Station' }}</div>
        <div class="req__actions">
          <button class="req-btn req-btn--reject" @click="armingReject(r.partitionId)">Decline</button>
          <button class="req-btn req-btn--approve" :class="r.action === 'arm' ? 'req-btn--approve-arm' : 'req-btn--approve-td'" @click="armingApprove(r.partitionId)">Approve</button>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script>
import Dialog from '@/components/Dialog'
import { formatApprovalTimeout } from '@/models/bypass'

export default {
  name: 'bypass-approval-dialog',
  components: { Dialog },
  data: function () {
    return {
      shieldSvg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"/><line x1="8.5" y1="15" x2="15.5" y2="9"/></svg>',
      // User dismissed the current batch — they can still act from the bypass list.
      dismissed: false,
    }
  },
  watch: {
    awaitingCount: function (now, prev) {
      // Re-surface when a new request arrives; reset once everything is resolved.
      if (now > prev || now === 0) this.dismissed = false
    },
  },
  computed: {
    // Approval permission (resource 14, mode 2): only roles that can respond to
    // monitoring-center requests get the approval prompt.
    canApprove: function () {
      return this.$store.getters.canRespondApproval
    },
    // Everything currently on screen, so the site can be read off the DATA.
    allRequests: function () {
      return [].concat(this.requests, this.clearRequests, this.tdRequests, this.armingRequests)
    },
    // MQTT only carries site_id (never a site name), so the name is always
    // resolved locally from the site list (getSites/getSite). Resolve it from the
    // site id the records themselves carry — not from selectedSiteId — so this
    // stays correct if we ever subscribe to more than one site's topic.
    siteName: function () {
      const first = this.allRequests[0]
      const sid = first && first.siteId != null ? first.siteId : this.$store.getters.selectedSiteId
      const site = this.$store.getters.site(sid)
      return (site && site.siteName) || ''
    },
    open: function () {
      return this.awaitingCount > 0 && !this.dismissed && this.canApprove
    },
    requests: function () {
      return this.$store.getters.bypassRequests || []
    },
    clearRequests: function () {
      return this.$store.getters.bypassClearRequests || []
    },
    tdRequests: function () {
      return this.$store.getters.tempDisarmRequests || []
    },
    armingRequests: function () {
      return this.$store.getters.armingRequests || []
    },
    // Combined pending count drives open / dismiss re-surfacing for all types.
    awaitingCount: function () {
      return this.$store.getters.bypassAwaitingCount +
        this.$store.getters.bypassClearAwaitingCount +
        this.$store.getters.tempDisarmAwaitingCount +
        this.$store.getters.armingAwaitingCount
    },
    now: function () {
      return this.$store.getters.bypassNow
    },
    tdNow: function () {
      return this.$store.getters.tempDisarmNow
    },
    nameMap: function () {
      const map = {}
      ;(this.$store.getters.cameraList || []).forEach(c => {
        map[c.deviceId] = c.name || c.model || 'No name'
      })
      return map
    },
    partitionNameMap: function () {
      const map = {}
      const sid = this.$store.getters.selectedSiteId
      ;(this.$store.getters.sitePartitions(sid) || []).forEach(p => {
        map[String(p.id)] = p.name || 'No Name'
      })
      return map
    },
  },
  methods: {
    onClose: function () {
      this.dismissed = true
    },
    nameOf: function (deviceId) {
      return this.nameMap[deviceId] || 'Camera'
    },
    durationLabelOf: function (r) {
      // Use the label the mapper derived from the real request (until_next_disarm /
      // requested_duration). Do NOT recompute from durationKey — it's null on
      // API-mapped requests and would always fall back to "Until Next Disarm".
      return r.durationLabel || '—'
    },
    remainingMs: function (r) {
      return Math.max(0, (r.requestExpiresAt || 0) - this.now)
    },
    remainingText: function (r) {
      return formatApprovalTimeout(this.remainingMs(r)) + ' left'
    },
    approve: function (deviceId) {
      this.$store.dispatch('approveBypassRequest', { deviceId })
    },
    reject: function (deviceId) {
      this.$store.dispatch('rejectBypassRequest', { deviceId })
    },
    clearRemainingMs: function (r) {
      return Math.max(0, (r.requestExpiresAt || 0) - this.now)
    },
    clearRemainingText: function (r) {
      return formatApprovalTimeout(this.clearRemainingMs(r)) + ' left'
    },
    clearApprove: function (deviceId) {
      this.$store.dispatch('approveBypassClearRequest', { deviceId })
    },
    clearReject: function (deviceId) {
      this.$store.dispatch('rejectBypassClearRequest', { deviceId })
    },
    // ── Temporary-disarm requests ──
    partitionNameOf: function (partitionId) {
      return this.partitionNameMap[String(partitionId)] || 'Partition'
    },
    tdDurationLabelOf: function (r) {
      return r.durationLabel || '—'
    },
    tdRemainingMs: function (r) {
      return Math.max(0, (r.requestExpiresAt || 0) - this.tdNow)
    },
    tdRemainingText: function (r) {
      return formatApprovalTimeout(this.tdRemainingMs(r)) + ' left'
    },
    tdApprove: function (partitionId) {
      this.$store.dispatch('approveTempDisarmRequest', { partitionId })
    },
    tdReject: function (partitionId) {
      this.$store.dispatch('rejectTempDisarmRequest', { partitionId })
    },
    armingRemainingMs: function (r) {
      return Math.max(0, (r.requestExpiresAt || 0) - this.tdNow)
    },
    armingRemainingText: function (r) {
      return formatApprovalTimeout(this.armingRemainingMs(r)) + ' left'
    },
    armingApprove: function (partitionId) {
      this.$store.dispatch('approveArmingRequest', { partitionId })
    },
    armingReject: function (partitionId) {
      this.$store.dispatch('rejectArmingRequest', { partitionId })
    },
  },
}
</script>

<style scoped>
.site-line {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 13px;
  color: var(--ion-text-medium);
}
.site-line__icon {
  flex-shrink: 0;
  font-size: 15px;
  opacity: 0.7;
}
.site-line__name {
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.intro {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--chekt-bypass-0);
  border: 1px solid var(--chekt-bypass-2);
  font-size: 13.5px;
  line-height: 19px;
  color: var(--chekt-bypass-8);
  margin-bottom: 14px;
}
.intro__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 10px;
  color: #ffffff;
  background: var(--chekt-bypass);
}
.reqs {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.req {
  padding: 14px;
  border-radius: 14px;
  background: var(--ion-color-medium);
  box-shadow: 0 0 0 1px rgba(245, 166, 35, 0.35) inset;
}
.req__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.req__name {
  font-size: 16px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.req__timer {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 4px 9px;
  border-radius: 999px;
  background: var(--chekt-bypass-1);
  color: var(--chekt-bypass-7);
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.req__timer ion-icon {
  font-size: 15px;
}
.req__timer--urgent {
  background: var(--chekt-bypass-2);
  color: var(--chekt-bypass-8);
}
.req__detail {
  margin-top: 8px;
  font-size: 14px;
  color: var(--ion-text-color);
}
.req__by {
  margin-top: 3px;
  font-size: 12px;
  opacity: 0.55;
}
.req__actions {
  display: flex;
  gap: 10px;
  margin-top: 14px;
}
.req-btn {
  flex: 1;
  padding: 11px 0;
  border-radius: 11px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: all 0.15s ease;
}
.req-btn--reject {
  background: transparent;
  border-color: rgba(0, 0, 0, 0.22);
  color: #333333;
}
.req-btn--approve {
  background: var(--chekt-bypass);
  color: #ffffff;
}

/* Temporary-disarm requests: disarmed-green accent to tell them apart from bypass */
.req--td {
  box-shadow: 0 0 0 1px rgba(30, 166, 117, 0.4) inset;
}
.req__timer--td {
  background: rgba(30, 166, 117, 0.16);
  color: var(--ion-color-tertiary);
}
.req__timer--td.req__timer--urgent {
  background: rgba(30, 166, 117, 0.28);
}
.req-btn--approve-td {
  background: var(--ion-color-tertiary);
  color: #ffffff;
}

/* Arm requests: armed-orange accent (secondary) to tell them apart from disarm */
.req--arm {
  box-shadow: 0 0 0 1px rgba(255, 127, 22, 0.4) inset;
}
.req__timer--arm {
  background: rgba(255, 127, 22, 0.16);
  color: var(--ion-color-secondary);
}
.req__timer--arm.req__timer--urgent {
  background: rgba(255, 127, 22, 0.28);
}
.req-btn--approve-arm {
  background: var(--ion-color-secondary);
  color: #ffffff;
}
</style>
